import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { userService, type UserProfile, type UserProfileResponse } from '@/services'
import { useAuth } from '@/auth'
import { Loader2, RefreshCw, Unlink } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { KeyMetricsGrid, CoreThemesList, AudienceTargetingBlock } from '@/components/accounts'
import { useToast } from '@/hooks/use-toast'
import { refreshAnalyticsProfile } from '@/utils/analytics-refresh'
import { formatDate } from '@/utils/saved-analytics'

type State = {
  loading: boolean
  profile: UserProfile | null
}

export function InstagramOverview() {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [state, setState] = useState<State>({ loading: true, profile: null })
  const [username, setUsername] = useState('')
  const [saving, setSaving] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const { toast } = useToast()

  const { data, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => userService.getProfile(),
    enabled: isAuthenticated && !authLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Refetch once when component mounts
  })

  // Mirror prior local state shape for minimal changes
  if (!authLoading && isAuthenticated && !profileLoading && state.loading) {
    const profile = (data as UserProfileResponse | undefined)?.profile || null
    setState({ loading: false, profile })
    if (profile?.instagramUsername) setUsername(profile.instagramUsername)
  }

  const hasUsername = !!state.profile?.instagramUsername
  const analytics: any = (state.profile as any)?.instagramAnalytics ?? (state.profile as any)?.InstagramAnalytics
  const hasAnalytics = !!analytics

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    try {
      const res = await userService.setInstagramUsername(username)
      setState((prev) => ({ ...prev, profile: res.profile || prev.profile }))
    } catch (e: any) {
      const detail = e?.response?.detail || e?.response?.message || e?.message
      if (e?.status === 400 && typeof detail === 'string' && detail.includes('No available analytics remaining')) {
        toast({ title: 'Limit reached', description: 'No available analytics remaining.', variant: 'destructive' })
      } else {
        toast({ title: 'Error', description: 'Failed to save username.', variant: 'destructive' })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleRefresh = async () => {
    if (!state.profile?.ID || isRefreshing) return

    setIsRefreshing(true)
    
    try {
      const result = await refreshAnalyticsProfile(state.profile.ID)
      
      if (result.success) {
        toast({
          title: 'Analytics Refresh Started',
          description: result.message,
        })
      } else {
        toast({
          title: 'Refresh Failed',
          description: result.message,
          variant: 'destructive'
        })
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message
      if (error?.response?.status === 400 && typeof detail === 'string' && detail.includes('No available analytics remaining')) {
        toast({ title: 'Limit reached', description: 'No available analytics remaining.', variant: 'destructive' })
      } else {
        console.error('Failed to refresh analytics:', error)
        toast({
          title: 'Error',
          description: 'Failed to refresh analytics. Please try again.',
          variant: 'destructive'
        })
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDisconnect = async () => {
    if (isDisconnecting) return

    setIsDisconnecting(true)
    
    try {
      const result = await userService.setInstagramUsername('')
      
      if (result.success) {
        toast({
          title: 'Instagram Disconnected',
          description: 'Your Instagram account has been disconnected successfully.',
        })
        
        // Update local state to trigger UI refresh
        setState(prev => ({
          ...prev,
          profile: prev.profile ? { ...prev.profile, instagramUsername: '' } : null
        }))
        setUsername('')
      } else {
        toast({
          title: 'Disconnect Failed',
          description: 'Failed to disconnect Instagram account. Please try again.',
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Failed to disconnect Instagram:', error)
      toast({
        title: 'Error',
        description: 'Failed to disconnect Instagram account. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsDisconnecting(false)
    }
  }

  if (authLoading || profileLoading || state.loading) {
    return (
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-[180px]" />
          </div>
          <Skeleton className="h-4 w-[220px]" />
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!hasUsername) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Add your Instagram</CardTitle>
          <CardDescription>Connect your IG handle to unlock insights</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input placeholder="@username" value={username} onChange={(e) => setUsername(e.target.value)} disabled={saving} />
          <Button onClick={handleSave} disabled={saving || username.trim().length === 0}>
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (hasUsername && !hasAnalytics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your content insights will appear here</CardTitle>
          <CardDescription>We’ll populate analytics once processed for @{state.profile?.instagramUsername}</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const insights: any = analytics || {}
  const toNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && isFinite(value)) return value
    if (typeof value === 'string') {
      const n = parseFloat(value)
      return isNaN(n) ? null : n
    }
    if (value && typeof value === 'object') {
      try {
        const s = String(value as any)
        const n = parseFloat(s)
        return isNaN(n) ? null : n
      } catch {
        return null
      }
    }
    return null
  }

  const followers = toNumber(insights?.profile_overview?.followers) ?? 0
  const totalPostsAnalyzed = toNumber(insights?.profile_overview?.total_posts_analyzed) ?? 0
  const postsPerDay = toNumber(insights?.posting_activity?.posts_per_day) ?? 0
  const spanDays = toNumber(insights?.posting_activity?.span_days) ?? 0
  const erNumber = toNumber(insights?.engagement_metrics?.avg_engagement_rate)
  const engagementRate = erNumber !== null ? `${(erNumber * 100).toFixed(3)}%` : '—'

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>@{state.profile?.instagramUsername}</CardTitle>
            {insights?.processing_timestamp && (
              <Badge variant="secondary" className="text-xs">
                Updated {formatDate(insights.processing_timestamp)}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isDisconnecting}
              className="shrink-0"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={isRefreshing || isDisconnecting}
              className="shrink-0"
            >
              <Unlink className={`h-4 w-4 mr-2 ${isDisconnecting ? 'animate-pulse' : ''}`} />
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <KeyMetricsGrid 
          followers={followers}
          totalPostsAnalyzed={totalPostsAnalyzed}
          postsPerDay={postsPerDay}
          engagementRate={engagementRate}
          spanDays={spanDays}
        />
        {(() => {
          const coreThemes = insights?.captions_insights?.account_insights?.core_themes
          const primaryTarget = insights?.captions_insights?.account_insights?.audience_targeting?.primary
          const secondaryTarget = insights?.captions_insights?.account_insights?.audience_targeting?.secondary
          const hasThemes = Array.isArray(coreThemes) && coreThemes.length > 0
          const hasTargeting = !!(primaryTarget || secondaryTarget)
          if (!hasThemes && !hasTargeting) return null
          return (
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <CoreThemesList themes={coreThemes} limit={2} showHeader={true} />
              <AudienceTargetingBlock 
                primary={primaryTarget}
                secondary={secondaryTarget}
                showHeader={true}
              />
            </div>
          )
        })()}
      </CardContent>
    </Card>
  )
}

// Old theses rendering removed; replaced by CoreThemesList and AudienceTargetingBlock


